import { LinkIcon, PencilSquareIcon, XMarkIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { HeaderSelectionModal } from 'components/LoanPipelineHeaderConfigs/HeaderSelectionModal'
import type { InputType } from 'config'
import { accountTypes } from 'config/user.type.constants'
import { isPlaceholderSelected } from 'pages/Loan/Reports/types'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { addNewTask, updateTask } from 'services'
import { Modal, Tooltip } from 'stories/components'
import { formatDateYMD, getLoanDisplayNo, InputConvert, InputValidate, openAuditLog } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import type { Task } from '../constants'
import { TaskComments } from '../taskComments'

export const defaultInputs = (): Record<string, InputType> => {
  return {
    loanNumber: {
      inputType: 'text',
      type: 'number',
      title: 'Loan Number',
      value: '',
      required: false,
    },
    description: {
      inputType: 'text',
      type: 'text',
      title: 'Description',
      value: '',
      required: true,
    },
    assignedTo: {
      inputType: 'InputWithButton',
      type: 'text',
      title: 'Assigned To',
      value: [],
      required: true,
      modalTitle: 'Assigned Task to...',
      options: accountTypes,
    },
    assignedToManager: {
      inputType: 'InputWithButton',
      type: 'text',
      title: 'Assigned To Manager',
      value: [],
      required: false,
      modalTitle: 'Assigned Task to Manager...',
      options: accountTypes,
    },
    dueDate: {
      inputType: 'text',
      type: 'date',
      title: 'Due Date',
      value: '',
      required: true,
    },
    replyCcUser: {
      inputType: 'InputWithButton',
      type: 'text',
      title: 'Reply CC User',
      value: [],
      required: false,
      modalTitle: 'Reply CC User...',
      options: accountTypes,
    },
    note: {
      inputType: 'textarea',
      type: 'text',
      title: 'Note',
      value: '',
      span: 2,
      required: false,
    },
    files: {
      inputType: 'filetable',
      filePath: 'task/new',
      title: 'Files',
      span: 2,
      required: false,
      showStatus: false,
      showCategory: false,
    },
  }
}

export const AddTaskModal = (props: {
  assignedToList: Record<string, string>
  onClose: any
  projectId: number
  loanNumber?: number
  byteproFileName?: number
  task: Task | null
  defaultData?: Record<string, any>
}) => {
  const [loading, setLoading] = useState(false)
  const [inputs, setInputs] = useState(defaultInputs())
  const [dueDateSource, setDueDateSource] = useState('')
  const [isShowDateSelectModal, setShowDateSelectModal] = useState(false)

  const hasLoanContext = !!(props.loanNumber || props.task?.loanNumber)

  useEffect(() => {
    const { task } = props
    if (!task) {
      let newInputs = cloneDeep(inputs)
      if (props.loanNumber) {
        newInputs.loanNumber.value = getLoanDisplayNo({
          byteproFileName: props.byteproFileName,
          loanNumber: props.loanNumber,
        })
        newInputs.loanNumber.disabled = true
        ;(newInputs.files as any).filePath = `task/${props.loanNumber}`
      }
      if (props.defaultData) {
        Object.keys(props.defaultData).map((key) => {
          if (newInputs[key] && props.defaultData) {
            newInputs[key].value = props.defaultData[key]
          }
        })
        if (props.defaultData.dueDateSource) setDueDateSource(String(props.defaultData.dueDateSource))
      }
      setInputs(newInputs)
      return
    }

    let newInputs = cloneDeep(inputs)
    newInputs.loanNumber.value = getLoanDisplayNo({
      byteproFileName: task.byteproFileName,
      loanNumber: task.loanNumber,
    })
    newInputs.description.value = task.description
    newInputs.assignedTo.value = task.assignedTo.map((item: any) => item.value)
    newInputs.assignedToManager.value = task.assignedToManager.map((item: any) => item.value)
    newInputs.replyCcUser.value = task.replyCcUser
    newInputs.dueDate.value = formatDateYMD(task.dueDate)
    newInputs.note.value = task.note
    newInputs.loanNumber.disabled = true
    ;(newInputs.files as any).filePath = `task/${task.loanNumber}`
    newInputs.files.value = task.files
    setDueDateSource(task.dueDateSource || '')
    setInputs(newInputs)
  }, [props.task])

  const onChange = (key: string, value: string) => {
    let newInputs = cloneDeep(inputs)
    value = InputConvert(newInputs[key], value)
    newInputs[key].error = InputValidate({ ...newInputs[key], value })
    newInputs[key].value = value
    if (key === 'dueDate' && dueDateSource) {
      setDueDateSource('')
    }
    setInputs(newInputs)
  }

  const onSelectLoanDate = (value: string) => {
    setShowDateSelectModal(false)
    setDueDateSource(value)
    let newInputs = cloneDeep(inputs)
    // Keep a date for required validation; backend re-resolves from dueDateSource on save.
    if (!newInputs.dueDate.value) newInputs.dueDate.value = formatDateYMD()
    newInputs.dueDate.error = ''
    setInputs(newInputs)
  }

  const clearDueDateLink = () => {
    setDueDateSource('')
  }

  const onSubmit = async () => {
    let hasError = false

    let newInputs = cloneDeep(inputs)
    const data: Record<string, any> = props.defaultData || {}
    for (const key in inputs) {
      const input = { ...newInputs[key] }
      if (key === 'dueDate' && dueDateSource) input.required = false
      newInputs[key].error = InputValidate({ ...input, value: newInputs[key].value })
      data[key] = newInputs[key].value
      if (newInputs[key].error) hasError = true
    }
    setInputs(newInputs)
    if (hasError) return

    data.projectId = props.projectId
    if (props.loanNumber) data.loanNumber = props.loanNumber
    else if (props.task?.loanNumber) data.loanNumber = props.task.loanNumber
    data.dueDate = formatDateYMD(data.dueDate || formatDateYMD())
    data.dueDateSource = dueDateSource || ''

    setLoading(true)

    if (props.task) {
      updateTask(props.task.id, data)
        .then(() => {
          props.onClose(true)
        })
        .catch(() => setLoading(false))
    } else
      addNewTask(data)
        .then(() => {
          toast('A new task is added', { type: 'info' })
          props.onClose(true)
        })
        .catch(() => setLoading(false))
  }

  const onClose = () => {
    props.onClose()
  }

  const showHistory = (key: string) => {
    if (!props.task) return
    openAuditLog({
      table: 'Task',
      field: inputs[key]?.title || key,
      keys: { taskId: props.task.id, field: key },
    })
  }

  return (
    <Modal
      title={!props.task ? 'Add Task' : `Task Edit - ${props.task.no}`}
      titleOkay={props.task ? 'Update' : 'Save'}
      loading={loading}
      onClose={onClose}
      onOk={onSubmit}
      isOpen
    >
      <>
        <div className="w-144 grid gap-4 md:grid-cols-2 grid-cols-1 mb-3">
          {Object.keys(inputs).map((key, index) => {
            let input = inputs[key]
            if (input.inputType == 'InputWithButton') input.options = props.assignedToList
            input.history = props.task ? true : false

            if (key === 'dueDate') {
              input.additionalElements = hasLoanContext ? (
                <div className="flex gap-1 items-center">
                  <Tooltip message="Link due date to a loan date (e.g. Closing Date)">
                    <PencilSquareIcon
                      className="w-4 h-4 cursor-pointer text-shade-blue"
                      onClick={() => setShowDateSelectModal(true)}
                    />
                  </Tooltip>
                  {!!dueDateSource && (
                    <Tooltip message="Clear loan date link">
                      <XMarkIcon className="w-4 h-4 cursor-pointer text-gray-500" onClick={clearDueDateLink} />
                    </Tooltip>
                  )}
                </div>
              ) : null
            }

            return (
              <div className={`input md:col-span-${input.span || 1}`} key={index}>
                <RenderInput
                  input={input}
                  Key={key}
                  onChange={onChange}
                  showHistory={props.task ? showHistory : undefined}
                />
                {key === 'dueDate' && !!dueDateSource && (
                  <div className="mt-1 flex items-start gap-1 text-xs text-shade-blue">
                    <LinkIcon className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <span>
                      Linked to <span className="font-medium">{dueDateSource}</span> — updates when that date
                      changes
                    </span>
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {props.task && (
          <div className="w-144 mt-6">
            <TaskComments taskId={props.task.id} />
          </div>
        )}

        {hasLoanContext && (
          <HeaderSelectionModal
            isOpen={isShowDateSelectModal}
            multiselect={false}
            type="date"
            onClose={() => setShowDateSelectModal(false)}
            onSelectPlaceholder={(value) => onSelectLoanDate(value)}
            onItemsSubmit={() => {}}
            selectedCell={dueDateSource}
            checkSelected={(v: string, title: string) => isPlaceholderSelected(v, title, dueDateSource)}
          />
        )}
      </>
    </Modal>
  )
}
